/**
 * Risk tiering for confirmation dialogs (see components/ui/confirm-dialog.tsx).
 *
 * Encodes the project policy that production-state mutations get heavier
 * confirmation than recoverable actions:
 *   - moderate → outline action button (recoverable: remove override, reject)
 *   - high     → destructive action + warning (terminate/delete)
 *   - critical → destructive + warning + type-the-phrase (irreversible high-impact)
 *
 * Kept here (not in the .tsx) so the pure decision logic is unit-tested by the
 * web `test` script, which runs src/lib.
 */
export type ConfirmRisk = 'moderate' | 'high' | 'critical';

/** Action-button variant for a risk tier. */
export function confirmActionVariant(risk: ConfirmRisk): 'outline' | 'destructive' {
  return risk === 'moderate' ? 'outline' : 'destructive';
}

/** Whether the confirm button is enabled given the typed phrase (critical tier only). */
export function confirmPhraseSatisfied(
  risk: ConfirmRisk,
  confirmPhrase: string | undefined,
  typed: string
): boolean {
  if (risk !== 'critical' || !confirmPhrase) return true;
  return typed.trim() === confirmPhrase.trim();
}

/**
 * Whether toggling a codebase's env-key consent needs a confirmation dialog.
 * Granting (currently denied) is immediate; revoking (currently allowed) is
 * confirmed — future runs are blocked if .env carries sensitive keys. Extracted
 * + tested so the security-relevant asymmetry can't be silently inverted.
 */
export function envKeyToggleRequiresConfirm(currentlyAllowed: boolean): boolean {
  return currentlyAllowed;
}
